import React from 'react';
import { Link, MemoryRouter, Route, Switch, useHistory } from 'react-router-dom';
import { Input, Label } from '@rebass/forms';
import { Button } from 'rebass';

import NiceModal from 'components/NiceModal';
import { usePublicResendOptInConfirmationEmailMutation } from 'api/usePublicResendOptInConfirmationEmailMutation';
import { useToastHelper } from 'hooks/useToastHelper';
import useForm from 'hooks/useForm';
import { usePublicChangePersonEmailMutation } from 'api/usePublicChangePersonEmailMutation';
import services from 'services/services';

/**
 * @typedef {Object} Addressee
 * @property {string} email
 * @property {string} person_opt_in_token
 */

/**
 * @typedef {Object} MainPageProps
 * @property {Addressee} addressee
 */

/**
 * Main page.
 * @param {MainPageProps} props
 */
function MainPage({ addressee }) {
    const { addErrorToast, addSuccessToast } = useToastHelper();
    const { mutate: resendConfirmation } = usePublicResendOptInConfirmationEmailMutation({
        onSuccess: () => {
            addSuccessToast('Confirmation email has been resent.');
        },
        onError: error => {
            if (error.response.status === 429) {
                // 5min gap
                addErrorToast('Please wait a few minutes before trying again.');
            } else {
                addErrorToast('Something went wrong. Please try again later.');
            }
        }
    });

    const submitResend = () => {
        resendConfirmation(addressee.person_opt_in_token);
    };

    return (
        <>
            <p>
                To activate your account, please check your inbox for a confirmation email we sent to:{' '}
                <b>{addressee.email}</b>
            </p>
            <p>Click the link inside that email to complete your registration.</p>
            <hr />
            <p>
                Wrong email? <Link to="/change">Click here to change it</Link>.
            </p>
            <p>
                No email? Check your spam folder or{' '}
                <button
                    type="button"
                    className="bg-transparent border-0 p-0 cursor-pointer color-primary"
                    onClick={submitResend}
                >
                    resend the email
                </button>
            </p>
        </>
    );
}

/**
 * @typedef {Object} ChangeEmailPageProps
 * @property {Addressee} addressee
 * @property {(email: string) => void} onUpdate
 */

/**
 * Update email page.
 * @param {ChangeEmailPageProps} props
 */
function ChangeEmailPage({ addressee, onUpdate }) {
    const history = useHistory();
    const { addErrorToast, addSuccessToast } = useToastHelper();
    const { mutate: changeEmail, isLoading: isEmailChanging } = usePublicChangePersonEmailMutation({
        onSuccess: (_, variables) => {
            addSuccessToast('Email has been changed and email confirmation email has been resent.');
            history.push('/');
            onUpdate?.(variables.email);
        },
        onError: error => {
            const message = services.parseAndTrackXhrErrors(error);

            if (error.response.status === 429) {
                // 5min gap
                addErrorToast('Please wait a few minutes before trying again.');
            } else {
                addErrorToast(message);
            }
        }
    });
    const { handleSubmit, register } = useForm({
        initialForm: {
            email: addressee.email
        }
    });

    const submitEmailChange = handleSubmit(data => {
        changeEmail({
            personOptInToken: addressee.person_opt_in_token,
            email: data.email
        });
    });

    return (
        <form onSubmit={submitEmailChange}>
            <Label htmlFor="email-input">E-mail</Label>
            <Input id="email-input" {...register('email')} defaultValue={addressee.email} />
            <div className="modal-actions">
                <Button
                    as={Link}
                    to="/"
                    variant="secondary-gray"
                    className="!mr-2"
                    style={{ padding: '5px 16px' }}
                    disabled={isEmailChanging}
                >
                    Cancel
                </Button>
                <Button type="submit" variant="primary" className="modal-primary !m-0" disabled={isEmailChanging}>
                    Update email
                </Button>
            </div>
        </form>
    );
}

/**
 * @typedef {Object} OptInConfirmationAnnouncementModalProps
 * @property {boolean} isOpen
 * @property {() => void} onClose
 * @property {Addressee} addressee
 * @property {(email: string) => void} onAddresseeUpdate
 */

/**
 * Modal to display an announcement to the user that they need to confirm their email.
 * @param {OptInConfirmationAnnouncementModalProps} props
 */
export function OptInConfirmationAnnouncementModal({ isOpen, onClose, addressee, onAddresseeUpdate }) {
    return (
        <NiceModal
            title="Almost there! Please confirm your email."
            style={{ content: { width: '640px' } }}
            isOpen={isOpen}
            onRequestClose={onClose}
        >
            <MemoryRouter>
                <Switch>
                    <Route exact path="/">
                        <MainPage addressee={addressee} />
                    </Route>
                    <Route path="/change">
                        <ChangeEmailPage addressee={addressee} onUpdate={onAddresseeUpdate} />
                    </Route>
                </Switch>
            </MemoryRouter>
        </NiceModal>
    );
}
